import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Popconfirm,
  Pagination,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PushpinFilled,
  ReloadOutlined,
  EnterOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { formatCST, nowCST, pickerToUTCISO } from '@/lib/time'
import { useDebounced, useIsMobile } from '@/lib/hooks'
import { DebouncedSearchInput } from '@/components/DebouncedSearchInput'
import { IpAddressLookup } from '@/components/IpAddressLookup'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminRecordActions } from '@/components/AdminRecordActions'

// ?anime_id=N 让其他页面（如 anime EditDrawer）能深链到"按番剧过滤的评论列表"
const searchSchema = z.object({
  anime_id: z.coerce.number().int().positive().optional().catch(undefined),
})

export const Route = createFileRoute('/_authed/comments')({
  component: CommentsPage,
  validateSearch: searchSchema,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type Status = 'visible' | 'hidden' | 'shadow'
type ModeratorKind = 'human' | 'ai' | null
type TabKey = 'unreviewed' | 'ai' | 'human' | 'all'
type SortKey = 'created_desc' | 'reports_desc' | 'likes_desc'

interface CommentRow {
  id: number
  content: string
  anime_id: number
  anime_title: string | null
  user_id: string | null
  legacy_user_id: number | null
  user_username: string | null
  user_display_name: string | null
  legacy_author_name: string | null
  parent_id: number | null
  status: Status
  moderator_kind: ModeratorKind
  moderated_at: string | null
  moderated_by: string | null
  moderation_note: string | null
  moderation_metadata: Record<string, unknown> | null
  report_count: number
  like_count: number
  dislike_count: number
  reply_count: number
  is_pinned: boolean
  ip_address?: string | null
  created_at: string
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<CommentRow>
  error?: string
}

interface TabCounts {
  all: number
  ai: number
  human: number
  unreviewed: number
}

interface CountsResult {
  ok: boolean
  counts?: TabCounts
  error?: string
}

interface AnimeOption {
  id: number
  title: string
}

/* ── i18n ───────────────────────────────────────────────────── */

const STATUS_LABELS: Record<Status, string> = {
  visible: '可见',
  hidden: '隐藏',
  shadow: '影子',
}

const STATUS_OPTIONS = (
  Object.entries(STATUS_LABELS) as Array<[Status, string]>
).map(([value, label]) => ({ value, label }))

const SORT_LABELS: Record<SortKey, string> = {
  created_desc: '最新发布',
  reports_desc: '举报最多',
  likes_desc: '点赞最多',
}

const TAB_META: Array<{
  key: TabKey
  label: string
  emptyTitle: string
  emptyHint: string
}> = [
  {
    key: 'unreviewed',
    label: '未审',
    emptyTitle: '没有未审评论了',
    emptyHint: '所有评论都已被 AI 或人工处理过。被举报的请去「举报受理」。',
  },
  {
    key: 'ai',
    label: 'AI 已处理',
    emptyTitle: 'AI 还没处理过任何评论',
    emptyHint: 'AI 边缘函数可能未运行；可在「未审」tab 手动处理。',
  },
  {
    key: 'human',
    label: '人工已审',
    emptyTitle: '尚未做过人工审核',
    emptyHint: '从「高举报」或「未审」tab 开始审第一条吧。',
  },
  {
    key: 'all',
    label: '全部',
    emptyTitle: '当前过滤下没有评论',
    emptyHint: '试着清空一些过滤条件。',
  },
]

function statusColor(s: Status) {
  switch (s) {
    case 'visible':
      return 'success'
    case 'hidden':
      return 'warning'
    case 'shadow':
      return 'magenta'
    default:
      return 'default'
  }
}

function authorDisplay(row: CommentRow): string {
  return (
    row.user_display_name?.trim() ||
    row.user_username?.trim() ||
    row.legacy_author_name?.trim() ||
    '匿名'
  )
}

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 50

function formatDateTime(
  t: string | null | undefined,
  fmt = 'YYYY-MM-DD HH:mm',
) {
  return formatCST(t, fmt)
}

function formatBadge(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function CommentsPage() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const { anime_id: animeIdParam } = Route.useSearch()

  // 默认 tab 改成「未审」（主动巡检视角）。被举报的评论由 /reports 独占受理
  const [tab, setTab] = useState<TabKey>('unreviewed')
  const [statusFilter, setStatusFilter] = useState<Status[]>([])
  const [animeId, setAnimeId] = useState<number | null>(animeIdParam ?? null)
  const [animeSearchInput, setAnimeSearchInput] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [sort, setSort] = useState<SortKey>('created_desc')
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState<CommentRow | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<number[]>([])
  const isMobile = useIsMobile()

  const BULK_HARD_CAP = 500

  const debouncedAnimeSearch = useDebounced(animeSearchInput.trim(), 250)

  useEffect(() => {
    setPage(1)
    // 切换 tab / 过滤后清空选择 —— 不同列表语义下选择无意义
    setSelectedKeys([])
  }, [
    tab,
    statusFilter,
    animeId,
    debouncedUserSearch,
    range?.[0]?.valueOf(),
    range?.[1]?.valueOf(),
    sort,
  ])

  // ── 主列表查询
  const listQuery = useQuery({
    queryKey: [
      'admin_comments',
      {
        tab,
        statusFilter,
        animeId,
        debouncedUserSearch,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
        sort,
        page,
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_comments', {
          p_tab: tab,
          p_status: statusFilter.length > 0 ? statusFilter : null,
          p_anime_id: animeId,
          p_user_search: debouncedUserSearch || null,
          p_min_reports: null,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
          p_sort: sort,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.rows ?? [], total: r.total ?? 0 }
    },
  })

  // ── tab 计数查询（与列表分离：只受过滤变化影响，不受 tab/sort/page 影响）
  const countsQuery = useQuery({
    queryKey: [
      'admin_comment_counts',
      {
        statusFilter,
        animeId,
        debouncedUserSearch,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
      },
    ],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_comment_tab_counts', {
          p_status: statusFilter.length > 0 ? statusFilter : null,
          p_anime_id: animeId,
          p_user_search: debouncedUserSearch || null,
          p_min_reports: null,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
        })
      if (error) throw error
      const r = data as CountsResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.counts ?? null
    },
    staleTime: 30_000,
  })

  // ── 番剧搜索 autocomplete
  const animeOptionsQuery = useQuery({
    queryKey: ['admin_comments_anime_search', debouncedAnimeSearch],
    enabled: debouncedAnimeSearch.length >= 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .ilike('search_title', `%${debouncedAnimeSearch}%`)
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(20)
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })

  // ── 当前选中的番剧名（用于在没有 search 输入时显示当前 anime title）
  const currentAnimeQuery = useQuery({
    queryKey: ['admin_comments_current_anime', animeId],
    enabled: !!animeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .eq('id', animeId!)
        .maybeSingle()
      if (error) throw error
      return data ?? null
    },
  })

  // ── 状态切换 mutation（行内 + 抽屉共用）
  const setStatusMutation = useMutation({
    mutationFn: async (params: {
      id: number
      status: Status
      note: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_comment_status', {
          p_id: params.id,
          p_status: params.status,
          p_note: params.note,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string; unchanged?: boolean }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r, vars) => {
      message.success(
        r.unchanged ? '已是该状态' : `已改为「${STATUS_LABELS[vars.status]}」`,
      )
      qc.invalidateQueries({ queryKey: ['admin_comments'] })
      qc.invalidateQueries({ queryKey: ['admin_comment_counts'] })
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  // ── 删除 mutation
  const deleteMutation = useMutation({
    mutationFn: async (params: { id: number; note: string }) => {
      if (params.note.trim().length < 5) throw new Error('reason_too_short')

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_comment', {
          p_id: params.id,
          p_note: params.note.trim(),
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        cascaded_reports?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(
        `已删除${r.cascaded_reports ? `（级联清掉 ${r.cascaded_reports} 条举报）` : ''}`,
      )
      qc.invalidateQueries({ queryKey: ['admin_comments'] })
      qc.invalidateQueries({ queryKey: ['admin_comment_counts'] })
      setDetail(null)
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('删除必须填写不少于 5 字的备注')
      } else {
        message.error(e.message || '删除失败')
      }
    },
  })

  // ── 批量改状态 mutation
  const bulkSetStatusMutation = useMutation({
    mutationFn: async (params: {
      ids: number[]
      status: Status
      note: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_set_comment_status', {
          p_ids: params.ids,
          p_status: params.status,
          p_note: params.note,
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        changed?: number
        requested?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r, vars) => {
      message.success(
        `批量改为「${STATUS_LABELS[vars.status]}」：影响 ${r.changed ?? 0} / ${r.requested ?? 0} 条`,
      )
      qc.invalidateQueries({ queryKey: ['admin_comments'] })
      qc.invalidateQueries({ queryKey: ['admin_comment_counts'] })
      setSelectedKeys([])
    },
    onError: (e: Error) => message.error(e.message || '批量保存失败'),
  })

  // ── 批量删除 mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (params: { ids: number[]; note: string }) => {
      if (params.note.trim().length < 5) throw new Error('reason_too_short')

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_delete_comments', {
          p_ids: params.ids,
          p_note: params.note.trim(),
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        deleted?: number
        requested?: number
        cascaded_reports_total?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(
        `已删除 ${r.deleted ?? 0} / ${r.requested ?? 0} 条${
          r.cascaded_reports_total
            ? `（级联清掉 ${r.cascaded_reports_total} 条举报）`
            : ''
        }`,
      )
      qc.invalidateQueries({ queryKey: ['admin_comments'] })
      qc.invalidateQueries({ queryKey: ['admin_comment_counts'] })
      setSelectedKeys([])
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('删除必须填写不少于 5 字的备注')
      } else {
        message.error(e.message || '批量删除失败')
      }
    },
  })

  // 批量改状态：弹 modal 收可选备注
  const handleBulkSetStatus = (status: Status) => {
    if (selectedKeys.length === 0) return
    if (selectedKeys.length > BULK_HARD_CAP) {
      message.error(`一次最多处理 ${BULK_HARD_CAP} 条`)
      return
    }
    let note = ''
    modal.confirm({
      title: `批量改为「${STATUS_LABELS[status]}」（${selectedKeys.length} 条）`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            备注会写入每条 audit log。空则记 admin_bulk_
            {status === 'visible' ? 'restore' : status}_comment。
          </Typography.Text>
          <Input.TextArea
            placeholder="备注（可选）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '执行',
      cancelText: '取消',
      onOk: () =>
        bulkSetStatusMutation.mutateAsync({
          ids: selectedKeys,
          status,
          note: note.trim() || null,
        }),
    })
  }

  // 批量删除：弹 modal 强收 ≥5 字备注
  // 手机吸底条「更多」菜单里状态类批量项的禁用条件（与桌面按钮一致）
  const bulkBusy =
    bulkSetStatusMutation.isPending ||
    bulkDeleteMutation.isPending ||
    selectedKeys.length > BULK_HARD_CAP

  const handleBulkDelete = () => {
    if (selectedKeys.length === 0) return
    if (selectedKeys.length > BULK_HARD_CAP) {
      message.error(`一次最多删除 ${BULK_HARD_CAP} 条`)
      return
    }
    let note = ''
    modal.confirm({
      title: `批量硬删（${selectedKeys.length} 条）？`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            不可恢复。每条 comment 的关联举报会被级联清掉。每条独立写 audit
            log。
          </Typography.Text>
          <Input.TextArea
            autoFocus
            placeholder="备注（必填 ≥5 字）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        if (note.trim().length < 5) {
          message.warning('备注必须 ≥5 字')
          return Promise.reject(new Error('reason_too_short'))
        }
        return bulkDeleteMutation.mutateAsync({
          ids: selectedKeys,
          note,
        })
      },
    })
  }

  // 行内 hover 快捷动作：删除走 modal 弹框收备注
  const handleRowDelete = (row: CommentRow) => {
    let note = ''
    modal.confirm({
      title: `硬删评论 #${row.id}？`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            不可恢复。触发器会级联清掉所有关联举报。审计日志会保留 200
            字内容摘要。
          </Typography.Text>
          <Input.TextArea
            autoFocus
            placeholder="备注（必填 ≥5 字）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        if (note.trim().length < 5) {
          message.warning('删除必须填写不少于 5 字的备注')
          return Promise.reject(new Error('reason_too_short'))
        }
        return deleteMutation.mutateAsync({ id: row.id, note })
      },
    })
  }

  // 行内 hover 状态切换：直接走 fallback reason
  const handleRowSetStatus = (row: CommentRow, status: Status) => {
    if (row.status === status) {
      message.info('已是该状态')
      return
    }
    setStatusMutation.mutate({ id: row.id, status, note: null })
  }

  /* ── 列定义 ───────────────────────────────────────────────── */

  const columns: ColumnsType<CommentRow> = useMemo(
    () => [
      {
        title: '内容',
        render: (_: unknown, row) => (
          <div className="comments-content-col">
            <Tooltip title={row.content} mouseEnterDelay={0.4}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{ marginBottom: 4 }}
              >
                {row.content}
              </Typography.Paragraph>
            </Tooltip>
            <Space
              size={6}
              style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}
            >
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                #{row.id}
              </span>
              {row.parent_id ? (
                <Tooltip title="回复评论">
                  <EnterOutlined style={{ fontSize: 12 }} />
                </Tooltip>
              ) : null}
              {row.is_pinned ? (
                <Tooltip title="置顶">
                  <PushpinFilled style={{ fontSize: 12, color: '#fadb14' }} />
                </Tooltip>
              ) : null}
            </Space>
          </div>
        ),
      },
      {
        title: '番剧',
        width: 160,
        render: (_: unknown, row) =>
          row.anime_title ? (
            <Typography.Text style={{ fontSize: 12 }} ellipsis>
              《{row.anime_title}》
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{row.anime_id}
            </Typography.Text>
          ),
      },
      {
        title: '作者',
        width: 160,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 13 }}>
              {authorDisplay(row)}
            </Typography.Text>
            {row.user_username ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                @{row.user_username}
              </Typography.Text>
            ) : row.legacy_user_id ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                legacy #{row.legacy_user_id}
              </Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: '状态',
        width: 110,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2} style={{ rowGap: 0 }}>
            <Tag color={statusColor(row.status)} style={{ marginRight: 0 }}>
              {STATUS_LABELS[row.status]}
            </Tag>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.moderator_kind === 'human'
                ? '人工'
                : row.moderator_kind === 'ai'
                  ? 'AI'
                  : '未审'}
              {row.moderated_at
                ? ` · ${formatCST(row.moderated_at, 'MM-DD')}`
                : ''}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '热度',
        width: 130,
        render: (_: unknown, row) => (
          <Space
            size={8}
            style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
          >
            <span
              style={{
                color: row.report_count > 0 ? '#cf1322' : undefined,
                fontWeight: row.report_count > 0 ? 600 : undefined,
              }}
            >
              举报 {row.report_count}
            </span>
            <span>赞 {row.like_count}</span>
            <span>回 {row.reply_count}</span>
          </Space>
        ),
      },
      {
        title: '时间',
        width: 110,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCST(row.created_at, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 160,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={2}>
            <div className="comments-row-actions">
              <Space size={0}>
                <Tooltip title={row.status === 'hidden' ? '已隐藏' : '隐藏'}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeInvisibleOutlined />}
                    disabled={row.status === 'hidden'}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRowSetStatus(row, 'hidden')
                    }}
                  />
                </Tooltip>
                <Tooltip title={row.status === 'shadow' ? '已影子' : '影子'}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    disabled={row.status === 'shadow'}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRowSetStatus(row, 'shadow')
                    }}
                  />
                </Tooltip>
                <Tooltip title="删除">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRowDelete(row)
                    }}
                  />
                </Tooltip>
              </Space>
            </div>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setDetail(row)
              }}
            >
              详情
            </Button>
          </Space>
        ),
      },
    ],
    [setStatusMutation, deleteMutation],
  )

  const tabEmptyMeta = TAB_META.find((t) => t.key === tab)
  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const emptyContent =
    listQuery.isLoading || listQuery.isFetching ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="加载中…" />
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical" size={4}>
            <Typography.Text strong>
              {tabEmptyMeta?.emptyTitle ?? '没有数据'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tabEmptyMeta?.emptyHint ?? ''}
            </Typography.Text>
          </Space>
        }
      />
    )

  // 番剧 Select options：搜索结果 + 当前选中的兜底（避免有 id 但下拉空时不显示名字）
  const animeSelectOptions: Array<AnimeOption> = useMemo(() => {
    const list = animeOptionsQuery.data ?? []
    const current = currentAnimeQuery.data
    if (current && !list.find((a) => a.id === current.id)) {
      return [current, ...list]
    }
    return list
  }, [animeOptionsQuery.data, currentAnimeQuery.data])

  return (
    <>
      <Card
        title="评论审核"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => {
                listQuery.refetch()
                countsQuery.refetch()
              }}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* Tab 行：标签 + count badge */}
          <div className="comments-tabs">
            <Segmented<TabKey>
              value={tab}
              onChange={(v) => setTab(v)}
              options={TAB_META.map(({ key, label }) => {
                const c = countsQuery.data ? countsQuery.data[key] : undefined
                return {
                  value: key,
                  label: (
                    <Space size={6}>
                      <span>{label}</span>
                      {c !== undefined ? (
                        <Badge
                          count={formatBadge(c)}
                          showZero
                          color={
                            c === 0
                              ? 'var(--ant-color-fill-secondary)'
                              : 'var(--ant-color-fill-tertiary)'
                          }
                          style={{
                            color:
                              c === 0
                                ? 'var(--ant-color-text-tertiary)'
                                : 'var(--ant-color-text)',
                            fontWeight: 500,
                          }}
                        />
                      ) : null}
                    </Space>
                  ),
                }
              })}
            />
          </div>

          {/* 过滤行：手机上只露搜索框，其余折叠在「筛选」后 */}
          <AdminFilterBar
            primary={
              <DebouncedSearchInput
                allowClear
                prefix={<SearchOutlined />}
                placeholder="用户名/昵称/旧站名"
                onDebouncedChange={setDebouncedUserSearch}
                style={{ width: 220 }}
              />
            }
            activeCount={
              (statusFilter.length > 0 ? 1 : 0) +
              (animeId != null ? 1 : 0) +
              (range ? 1 : 0) +
              (sort !== 'created_desc' ? 1 : 0)
            }
          >
            <Select<Status[]>
              mode="multiple"
              allowClear
              placeholder="状态过滤"
              style={{ minWidth: 180 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
              maxTagCount="responsive"
            />
            <Select<number | null>
              allowClear
              showSearch
              placeholder="番剧（输入搜索）"
              style={{ minWidth: 220 }}
              value={animeId ?? undefined}
              onChange={(v) => setAnimeId(v ?? null)}
              onSearch={setAnimeSearchInput}
              filterOption={false}
              loading={
                animeOptionsQuery.isFetching || currentAnimeQuery.isFetching
              }
              notFoundContent={
                debouncedAnimeSearch
                  ? animeOptionsQuery.isFetching
                    ? '搜索中…'
                    : '无匹配'
                  : '输入关键字搜索'
              }
              options={animeSelectOptions.map((a) => ({
                value: a.id,
                label: a.title,
              }))}
            />
            <DatePicker.RangePicker
              className="admin-filter-grid__full"
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={range}
              onChange={(r) =>
                setRange(r && r[0] && r[1] ? [r[0], r[1]] : null)
              }
              presets={[
                {
                  label: '今天',
                  value: [nowCST().startOf('day'), nowCST().endOf('day')],
                },
                {
                  label: '近 7 天',
                  value: [nowCST().subtract(7, 'day').startOf('day'), nowCST()],
                },
                {
                  label: '近 30 天',
                  value: [
                    nowCST().subtract(30, 'day').startOf('day'),
                    nowCST(),
                  ],
                },
              ]}
            />
            <Select<SortKey>
              value={sort}
              onChange={setSort}
              style={{ width: 140 }}
              options={Object.entries(SORT_LABELS).map(([value, label]) => ({
                value: value as SortKey,
                label,
              }))}
            />
          </AdminFilterBar>

          {/* 错态 banner */}
          {listQuery.error ? (
            <Alert
              type="error"
              showIcon
              closable
              message="加载评论失败"
              description={listQuery.error.message}
              action={
                <Button size="small" onClick={() => listQuery.refetch()}>
                  重试
                </Button>
              }
            />
          ) : null}

          {/* 批量操作条：选中 ≥1 时出现，sticky 紧贴 Tab/Filter 下方 */}
          {selectedKeys.length > 0 && !isMobile ? (
            <div
              className="admin-desktop-only"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'var(--ant-color-primary-bg)',
                border: '1px solid var(--ant-color-primary-border)',
                borderRadius: 6,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <Typography.Text strong>
                已选 {selectedKeys.length} 条
                {selectedKeys.length > BULK_HARD_CAP ? (
                  <Typography.Text type="danger" style={{ marginLeft: 8 }}>
                    （超过 {BULK_HARD_CAP} 上限）
                  </Typography.Text>
                ) : null}
              </Typography.Text>
              <Space size={4}>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleBulkSetStatus('visible')}
                  disabled={
                    bulkSetStatusMutation.isPending ||
                    bulkDeleteMutation.isPending ||
                    selectedKeys.length > BULK_HARD_CAP
                  }
                >
                  恢复可见
                </Button>
                <Button
                  size="small"
                  icon={<EyeInvisibleOutlined />}
                  onClick={() => handleBulkSetStatus('hidden')}
                  disabled={
                    bulkSetStatusMutation.isPending ||
                    bulkDeleteMutation.isPending ||
                    selectedKeys.length > BULK_HARD_CAP
                  }
                >
                  批量隐藏
                </Button>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleBulkSetStatus('shadow')}
                  disabled={
                    bulkSetStatusMutation.isPending ||
                    bulkDeleteMutation.isPending ||
                    selectedKeys.length > BULK_HARD_CAP
                  }
                >
                  批量影子
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={bulkDeleteMutation.isPending}
                  onClick={handleBulkDelete}
                  disabled={
                    bulkSetStatusMutation.isPending ||
                    selectedKeys.length > BULK_HARD_CAP
                  }
                >
                  批量删除
                </Button>
              </Space>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, marginLeft: 'auto' }}
              >
                单次上限 {BULK_HARD_CAP} 条
              </Typography.Text>
              <Button
                type="link"
                size="small"
                onClick={() => setSelectedKeys([])}
                style={{ padding: 0 }}
              >
                清空选择
              </Button>
            </div>
          ) : null}

          <div className="comments-table-wrap">
            <Table<CommentRow>
              rowKey="id"
              size="middle"
              loading={listQuery.isLoading}
              columns={columns}
              dataSource={rows}
              rowSelection={{
                selectedRowKeys: selectedKeys,
                onChange: (keys) => setSelectedKeys(keys as number[]),
                preserveSelectedRowKeys: true,
                columnWidth: 44,
              }}
              onRow={(row) => ({
                onClick: () => setDetail(row),
                style: { cursor: 'pointer' },
              })}
              scroll={{ x: 1240 }}
              locale={{ emptyText: emptyContent }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                showTotal: (totalRows) => `共 ${totalRows} 条`,
                onChange: (p) => setPage(p),
              }}
            />
          </div>

          <CommentMobileList
            rows={rows}
            loading={listQuery.isLoading || listQuery.isFetching}
            emptyContent={emptyContent}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            selectedKeys={selectedKeys}
            statusPending={setStatusMutation.isPending}
            deletePending={deleteMutation.isPending}
            onPageChange={setPage}
            onOpen={setDetail}
            onToggleSelect={(id) =>
              setSelectedKeys((keys) =>
                keys.includes(id)
                  ? keys.filter((key) => key !== id)
                  : [...keys, id],
              )
            }
            onSetStatus={handleRowSetStatus}
            onDelete={handleRowDelete}
          />
        </Space>

        {/* 手机批量操作：吸底条（放在 Space 外，sticky 才能相对整张卡片生效），主操作「隐藏 / 删除」露出，其余进「更多」 */}
        {selectedKeys.length > 0 ? (
          <div className="admin-mobile-bulkbar admin-mobile-only comments-mobile-bulkbar">
            <span className="admin-mobile-bulkbar__count">
              已选 {selectedKeys.length} 条
              {selectedKeys.length > BULK_HARD_CAP ? (
                <Typography.Text type="danger">
                  （超 {BULK_HARD_CAP}）
                </Typography.Text>
              ) : null}
            </span>
            <Button
              icon={<EyeInvisibleOutlined />}
              onClick={() => handleBulkSetStatus('hidden')}
              disabled={
                bulkSetStatusMutation.isPending ||
                bulkDeleteMutation.isPending ||
                selectedKeys.length > BULK_HARD_CAP
              }
            >
              隐藏
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={bulkDeleteMutation.isPending}
              onClick={handleBulkDelete}
              disabled={
                bulkSetStatusMutation.isPending ||
                selectedKeys.length > BULK_HARD_CAP
              }
            >
              删除
            </Button>
            <Dropdown
              trigger={['click']}
              placement="topRight"
              menu={{
                items: [
                  {
                    key: 'visible',
                    icon: <EyeOutlined />,
                    label: '批量恢复可见',
                    disabled: bulkBusy,
                  },
                  {
                    key: 'shadow',
                    icon: <EyeOutlined />,
                    label: '批量影子',
                    disabled: bulkBusy,
                  },
                  { type: 'divider' },
                  {
                    key: 'page',
                    label:
                      rows.length > 0 &&
                      rows.every((r) => selectedKeys.includes(r.id))
                        ? '取消选择本页'
                        : '全选本页',
                  },
                  { key: 'clear', label: '清空选择' },
                ],
                onClick: ({ key }) => {
                  if (key === 'clear') {
                    setSelectedKeys([])
                  } else if (key === 'page') {
                    const pageIds = rows.map((r) => r.id)
                    const allSelected = pageIds.every((id) =>
                      selectedKeys.includes(id),
                    )
                    setSelectedKeys((keys) =>
                      allSelected
                        ? keys.filter((k) => !pageIds.includes(k))
                        : Array.from(new Set([...keys, ...pageIds])),
                    )
                  } else {
                    handleBulkSetStatus(key as Status)
                  }
                },
              }}
            >
              <Button icon={<MoreOutlined />} aria-label="更多批量操作" />
            </Dropdown>
          </div>
        ) : null}
      </Card>

      <DetailDrawer
        row={detail}
        onClose={() => setDetail(null)}
        onSetStatus={(status, note) => {
          if (!detail) return
          setStatusMutation.mutate(
            { id: detail.id, status, note: note.trim() || null },
            {
              onSuccess: () => setDetail(null),
            },
          )
        }}
        onDelete={(note) => {
          if (!detail) return
          deleteMutation.mutate({ id: detail.id, note })
        }}
        statusPending={setStatusMutation.isPending}
        deletePending={deleteMutation.isPending}
      />
    </>
  )
}

/* ── 移动端卡片列表：手机上替代表格 ─────────────────────────── */

function CommentMobileList({
  rows,
  loading,
  emptyContent,
  page,
  pageSize,
  total,
  selectedKeys,
  statusPending,
  deletePending,
  onPageChange,
  onOpen,
  onToggleSelect,
  onSetStatus,
  onDelete,
}: {
  rows: CommentRow[]
  loading: boolean
  emptyContent: ReactNode
  page: number
  pageSize: number
  total: number
  selectedKeys: number[]
  statusPending: boolean
  deletePending: boolean
  onPageChange: (page: number) => void
  onOpen: (row: CommentRow) => void
  onToggleSelect: (id: number) => void
  onSetStatus: (row: CommentRow, status: Status) => void
  onDelete: (row: CommentRow) => void
}) {
  if (rows.length === 0) {
    return <div className="comments-mobile-list">{emptyContent}</div>
  }

  return (
    <div
      className="comments-mobile-list"
      aria-busy={loading}
      aria-label="评论移动端列表"
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {rows.map((row) => (
          <article
            key={row.id}
            className="comments-mobile-card"
            role="button"
            tabIndex={0}
            onClick={() => onOpen(row)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onOpen(row)
              }
            }}
          >
            <div className="comments-mobile-card__main">
              <Checkbox
                checked={selectedKeys.includes(row.id)}
                aria-label={`选择评论 #${row.id}`}
                onClick={(event) => event.stopPropagation()}
                onChange={() => onToggleSelect(row.id)}
              />
              <div className="comments-mobile-card__body">
                <Typography.Paragraph
                  ellipsis={{ rows: 3 }}
                  className="comments-mobile-card__content"
                >
                  {row.content}
                </Typography.Paragraph>

                <Space
                  wrap
                  size={[6, 4]}
                  className="comments-mobile-card__badges"
                >
                  <Tag color={statusColor(row.status)} style={{ margin: 0 }}>
                    {STATUS_LABELS[row.status]}
                  </Tag>
                  <Typography.Text type="secondary">#{row.id}</Typography.Text>
                  {row.parent_id ? (
                    <Typography.Text type="secondary">
                      回复 #{row.parent_id}
                    </Typography.Text>
                  ) : null}
                  {row.is_pinned ? (
                    <Tag
                      color="gold"
                      icon={<PushpinFilled />}
                      style={{ margin: 0 }}
                    >
                      置顶
                    </Tag>
                  ) : null}
                  {row.report_count > 0 ? (
                    <Tag color="red" style={{ margin: 0 }}>
                      举报 {row.report_count}
                    </Tag>
                  ) : null}
                </Space>
              </div>
            </div>

            <div className="comments-mobile-card__meta">
              <span>{authorDisplay(row)}</span>
              <span>
                {row.anime_title
                  ? `《${row.anime_title}》`
                  : `番剧 #${row.anime_id}`}
              </span>
              <span>{formatCST(row.created_at, 'MM-DD HH:mm')}</span>
            </div>

            <div className="comments-mobile-card__stats">
              <span>赞 {row.like_count}</span>
              <span>踩 {row.dislike_count}</span>
              <span>回复 {row.reply_count}</span>
              <span>
                {row.moderator_kind === 'human'
                  ? '人工已审'
                  : row.moderator_kind === 'ai'
                    ? 'AI 已审'
                    : '未审'}
              </span>
            </div>

            {/* 当前状态那一项不露出；剩下 3 个动作全部露出为紧凑按钮。
                主处理动作（隐藏；已隐藏时为恢复可见）排最后获得强调样式 */}
            <AdminRecordActions
              maxVisible={3}
              actions={[
                {
                  key: 'delete',
                  label: '删除',
                  icon: <DeleteOutlined />,
                  danger: true,
                  loading: deletePending,
                  onClick: () => onDelete(row),
                },
                ...(['shadow', 'visible', 'hidden'] as Status[])
                  .filter((s) => s !== row.status)
                  .sort((a, b) =>
                    row.status === 'hidden'
                      ? Number(a === 'visible') - Number(b === 'visible')
                      : 0,
                  )
                  .map((s) => ({
                    key: s,
                    label: s === 'visible' ? '可见' : STATUS_LABELS[s],
                    icon:
                      s === 'hidden' ? (
                        <EyeInvisibleOutlined />
                      ) : (
                        <EyeOutlined />
                      ),
                    disabled: statusPending,
                    onClick: () => onSetStatus(row, s),
                  })),
              ]}
            />
          </article>
        ))}

        {total > pageSize ? (
          <Pagination
            simple
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger={false}
            onChange={onPageChange}
          />
        ) : null}
      </Space>
    </div>
  )
}

/* ── 详情抽屉（重排：操作前置 + 折叠次要信息）─────────────────── */

function DetailDrawer({
  row,
  onClose,
  onSetStatus,
  onDelete,
  statusPending,
  deletePending,
}: {
  row: CommentRow | null
  onClose: () => void
  onSetStatus: (status: Status, note: string) => void
  onDelete: (note: string) => void
  statusPending: boolean
  deletePending: boolean
}) {
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [note, setNote] = useState('')
  const [noteVisible, setNoteVisible] = useState(false)

  useEffect(() => {
    setNote('')
    setNoteVisible(false)
  }, [row?.id])

  const confirmDelete = () => {
    if (note.trim().length < 5) {
      setNoteVisible(true)
      message.warning('删除必须填写不少于 5 字的备注')
      return
    }
    modal.confirm({
      title: '硬删该评论？',
      content:
        '不可恢复。触发器会级联清掉所有关联举报。审计日志会保留 200 字内容摘要。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => onDelete(note),
    })
  }

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      className="comments-detail-drawer"
      width={
        isMobile
          ? '100%'
          : Math.min(
              720,
              Math.floor(
                typeof window !== 'undefined' ? window.innerWidth * 0.85 : 720,
              ),
            )
      }
      title={
        row ? (
          <Space size={8}>
            <Tag color={statusColor(row.status)}>
              {STATUS_LABELS[row.status]}
            </Tag>
            <span>评论 #{row.id}</span>
          </Space>
        ) : (
          ''
        )
      }
      destroyOnHidden
    >
      {!row ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* 1) 内容 —— 始终最显眼 */}
          <div>
            <Typography.Paragraph
              style={{
                whiteSpace: 'pre-wrap',
                padding: 16,
                background: 'var(--ant-color-fill-tertiary)',
                borderRadius: 8,
                marginBottom: 8,
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {row.content}
            </Typography.Paragraph>
            <Space
              size={[10, 2]}
              wrap
              style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}
            >
              {row.anime_title ? (
                <span>《{row.anime_title}》</span>
              ) : (
                <span>#{row.anime_id}</span>
              )}
              <span>·</span>
              <span>{authorDisplay(row)}</span>
              {row.user_username ? <span>@{row.user_username}</span> : null}
              <span>·</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCST(row.created_at, 'YYYY-MM-DD HH:mm')}
              </span>
            </Space>
          </div>

          {/* 2) 操作 —— 第二位，above the fold */}
          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              操作
            </Typography.Title>
            <Radio.Group
              value={row.status}
              onChange={(e) => {
                const next = e.target.value as Status
                if (next === row.status) return
                onSetStatus(next, note)
              }}
              disabled={statusPending}
              optionType="button"
              buttonStyle="solid"
              options={(['visible', 'hidden', 'shadow'] as Status[]).map(
                (s) => ({
                  value: s,
                  label: STATUS_LABELS[s],
                }),
              )}
            />
            <Space
              style={{ marginLeft: 12 }}
              className="comments-drawer-delete"
            >
              <Popconfirm
                title="确认删除？"
                description="不可恢复。请先填写 ≥5 字备注。"
                open={false}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletePending}
                onClick={confirmDelete}
              >
                删除
              </Button>
            </Space>

            {noteVisible ? (
              <Input.TextArea
                autoFocus
                placeholder="备注（删除必填 ≥5 字；其他可选，写入审计日志）"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
                showCount
                style={{ marginTop: 12 }}
              />
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => setNoteVisible(true)}
                style={{ paddingLeft: 0, marginTop: 8 }}
              >
                + 添加备注（可选）
              </Button>
            )}
          </div>

          {/* 3) 高举报警示（如有）—— 紧跟操作 */}
          {row.report_count > 0 ? (
            <Alert
              type={row.report_count >= 3 ? 'error' : 'warning'}
              showIcon
              message={`该评论收到 ${row.report_count} 次举报`}
              description="按举报 reason 维度处理可去 /reports 找此 target。"
            />
          ) : null}
          {row.is_pinned ? (
            <Alert type="info" showIcon message="此评论当前为置顶" />
          ) : null}

          {/* 4) 次要信息 —— 折叠 */}
          <Collapse
            ghost
            items={[
              {
                key: 'meta',
                label: (
                  <Typography.Text strong>基础信息 / 互动</Typography.Text>
                ),
                children: (
                  <Descriptions
                    column={isMobile ? 1 : 2}
                    size="small"
                    bordered
                    items={[
                      {
                        label: '番剧',
                        children: row.anime_title ? (
                          <span>
                            《{row.anime_title}》（#{row.anime_id}）
                          </span>
                        ) : (
                          `#${row.anime_id}`
                        ),
                      },
                      {
                        label: '父评论',
                        children: row.parent_id
                          ? `#${row.parent_id}`
                          : '（顶层评论）',
                      },
                      {
                        label: '作者',
                        span: isMobile ? 1 : 2,
                        children: (
                          <Space direction="vertical" size={0}>
                            <Space size={6}>
                              <Avatar size={20}>
                                {authorDisplay(row).slice(0, 1)}
                              </Avatar>
                              <span>{authorDisplay(row)}</span>
                            </Space>
                            {row.user_username ? (
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 11 }}
                              >
                                @{row.user_username}
                              </Typography.Text>
                            ) : null}
                            {row.user_id ? (
                              <Typography.Text
                                type="secondary"
                                copyable={{ text: row.user_id }}
                                style={{
                                  fontSize: 11,
                                  fontFamily:
                                    'var(--ant-font-family-code, monospace)',
                                }}
                              >
                                {row.user_id.slice(0, 8)}…
                              </Typography.Text>
                            ) : row.legacy_user_id ? (
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 11 }}
                              >
                                legacy #{row.legacy_user_id}（未认领）
                              </Typography.Text>
                            ) : null}
                          </Space>
                        ),
                      },
                      {
                        label: '互动',
                        span: isMobile ? 1 : 2,
                        children: (
                          <Space
                            size={16}
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            <span
                              style={{
                                color:
                                  row.report_count > 0 ? '#cf1322' : undefined,
                              }}
                            >
                              举报 {row.report_count}
                            </span>
                            <span>赞 {row.like_count}</span>
                            <span>踩 {row.dislike_count}</span>
                            <span>回复 {row.reply_count}</span>
                          </Space>
                        ),
                      },
                      {
                        label: 'IP',
                        span: isMobile ? 1 : 2,
                        children: <IpAddressLookup ip={row.ip_address} />,
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'history',
                label: (
                  <Typography.Text strong>
                    审核历史
                    {row.moderator_kind ? (
                      <Typography.Text
                        type="secondary"
                        style={{
                          marginLeft: 8,
                          fontWeight: 'normal',
                          fontSize: 12,
                        }}
                      >
                        · {row.moderator_kind === 'human' ? '人工' : 'AI'} ·{' '}
                        {formatDateTime(row.moderated_at)}
                      </Typography.Text>
                    ) : (
                      <Typography.Text
                        type="secondary"
                        style={{
                          marginLeft: 8,
                          fontWeight: 'normal',
                          fontSize: 12,
                        }}
                      >
                        · 尚未审核
                      </Typography.Text>
                    )}
                  </Typography.Text>
                ),
                children: row.moderator_kind ? (
                  <Descriptions
                    column={1}
                    size="small"
                    bordered
                    items={[
                      {
                        label: '审核者',
                        children:
                          row.moderator_kind === 'human' ? '人工' : 'AI',
                      },
                      {
                        label: '审核时间',
                        children: formatDateTime(
                          row.moderated_at,
                          'YYYY-MM-DD HH:mm:ss',
                        ),
                      },
                      {
                        label: '操作者 UID',
                        children: row.moderated_by ?? '—',
                      },
                      {
                        label: '备注',
                        children: row.moderation_note ?? (
                          <Typography.Text type="secondary">
                            （无）
                          </Typography.Text>
                        ),
                      },
                      {
                        label: 'AI 元数据',
                        children: row.moderation_metadata ? (
                          <pre
                            style={{
                              margin: 0,
                              padding: 8,
                              background: 'var(--ant-color-fill-tertiary)',
                              borderRadius: 4,
                              fontSize: 11,
                              maxHeight: 200,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {JSON.stringify(row.moderation_metadata, null, 2)}
                          </pre>
                        ) : (
                          <Typography.Text type="secondary">
                            （无）
                          </Typography.Text>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Typography.Text type="secondary">尚未审核过</Typography.Text>
                ),
              },
            ]}
          />
        </Space>
      )}
    </Drawer>
  )
}
